import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogIn, LogOut, Users, Loader2, FileDown, Calendar, UserCog, Save, Upload, Trash2, FileSpreadsheet, Pencil, X, CalendarRange, Mail, ChevronLeft, ChevronRight, History, Check } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";
import { formatChildName } from "@/lib/names";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";

const FIXED_CLASSROOMS = ["Infants", "Toddlers", "Preschool", "School Age"];

const AGE_LABEL = {
  infant: "Infants",
  toddler: "Toddler",
  preschool: "Preschool",
  school_age: "School-age",
  // Legacy aliases
  toddler_young: "Toddler", toddler_older: "Toddler",
  preschool_25_3: "Preschool", preschool_3_4: "Preschool", preschool_4_5: "Preschool",
};

function mondayOf(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  return d.toISOString().slice(0, 10);
}

// iter-81 — a 7-tile strip that shows S M T W T F S with a strike-through on
// every day that is NOT part of the currently-selected week mode (or the
// center's saved operating days). This is the "cross line the rest of the
// week" visual the owner asked for. Sun=0..Sat=6.
const OP_DAYS_BY_MODE = { "5": [1,2,3,4,5], "7": [0,1,2,3,4,5,6], "2": [0,6] };
const OP_DAYS_BY_CENTER = { "mon-fri": [1,2,3,4,5], "sun-sat": [0,1,2,3,4,5,6], "sat-sun": [0,6] };
const DAY_LETTERS = ["S","M","T","W","T","F","S"];

function OperatingDaysStrip({ weekMode, operatingDays }) {
  // Union preferred: any day that's open per EITHER the current toggle OR
  // the center's saved pattern is drawn "on". Days closed by BOTH are
  // strike-through. This lets Sat-Sun centres who briefly switch the
  // toggle to Mon-Fri still see something reasonable.
  const openViaMode = new Set(OP_DAYS_BY_MODE[weekMode] || []);
  const openViaCenter = new Set(OP_DAYS_BY_CENTER[operatingDays] || []);
  return (
    <div className="mt-2 inline-flex items-center gap-1" data-testid="operating-days-strip">
      {DAY_LETTERS.map((letter, i) => {
        const on = openViaMode.has(i) && openViaCenter.has(i);
        return (
          <span
            key={i}
            data-testid={`op-day-${i}-${on ? "open" : "closed"}`}
            title={on ? "Open" : "Closed"}
            className={`inline-grid place-items-center w-6 h-6 rounded-md text-[11px] font-bold border ${
              on
                ? "bg-emerald-700 text-white border-emerald-700"
                : "bg-stone-100 text-stone-400 border-stone-200 line-through opacity-70"
            }`}
          >
            {letter}
          </span>
        );
      })}
    </div>
  );
}

export default function Attendance() {
  const [classrooms, setClassrooms] = useState([]);
  const [todayMap, setTodayMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [weekOf, setWeekOf] = useState(mondayOf(new Date()));
  // iter-81 — center's operating pattern powers the default. Owner picks it
  // in Center Settings; we cache the choice on load so the Attendance page
  // opens on the correct 5/7/2-day toggle. Fallback preserves prior behaviour.
  const [operatingDays, setOperatingDays] = useState("mon-fri");
  // iter-81o — holiday banner: fetches once and any observed holiday
  // falling on today auto-shows the CLOSED banner with its name.
  const [holidayToday, setHolidayToday] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/center/closed-today")
      .then((r) => { if (!cancelled) setHolidayToday(r.data?.holiday || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [weekMode, setWeekMode] = useState(() => localStorage.getItem("cc_week_mode") || "5"); // "5" (Mon-Fri), "7" (Sun-Sat), "2" (Sat-Sun weekends)
  const [activeRoom, setActiveRoom] = useState("Infants");
  const [teachers, setTeachers] = useState(null); // {Infants: {lead, assistant}, ...}
  const [teachersOpen, setTeachersOpen] = useState(false);
  const [savingTeachers, setSavingTeachers] = useState(false);
  // iter-44 — Excel/CSV import + manage
  const [imports, setImports] = useState([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);
  // iter-44 — edit popover for the latest event of a child
  const [editingChildId, setEditingChildId] = useState(null);
  const [editTs, setEditTs] = useState("");
  const [editKind, setEditKind] = useState("check_in");
  // History dialog — edit/delete ANY past attendance event for a child.
  const [historyChild, setHistoryChild] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRowDraft, setHistoryRowDraft] = useState({}); // {event_id: {ts, kind}}
  const [historySavingId, setHistorySavingId] = useState(null);

  const openHistory = async (child) => {
    setHistoryChild(child);
    setHistoryRowDraft({});
    setHistoryLoading(true);
    try {
      const r = await api.get(`/children/${child.id}/attendance-events`);
      setHistoryEvents(r.data?.events || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHistoryLoading(false); }
  };
  const reloadHistory = async () => {
    if (!historyChild) return;
    try {
      const r = await api.get(`/children/${historyChild.id}/attendance-events`);
      setHistoryEvents(r.data?.events || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const editHistoryRow = (ev) => {
    const d = new Date(ev.ts);
    const pad = (n) => String(n).padStart(2, "0");
    const ts = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setHistoryRowDraft((d2) => ({ ...d2, [ev.id]: { ts, kind: ev.kind } }));
  };
  const saveHistoryRow = async (ev) => {
    const draft = historyRowDraft[ev.id];
    if (!draft) return;
    setHistorySavingId(ev.id);
    try {
      await api.put(`/attendance/events/${ev.id}`, { ts: draft.ts, kind: draft.kind });
      toast.success("Attendance updated.");
      setHistoryRowDraft((d) => { const c = { ...d }; delete c[ev.id]; return c; });
      await reloadHistory();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHistorySavingId(null); }
  };
  const deleteHistoryRow = async (ev) => {
    if (!window.confirm(`Delete the ${ev.kind === "check_in" ? "check-in" : "check-out"} at ${new Date(ev.ts).toLocaleString()}? This cannot be undone.`)) return;
    setHistorySavingId(ev.id);
    try {
      await api.delete(`/attendance/events/${ev.id}`);
      toast.success("Attendance time removed.");
      await reloadHistory();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setHistorySavingId(null); }
  };

  const [bulkDate, setBulkDate] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkDeleteDay = async () => {
    if (!historyChild || !bulkDate) {
      toast.error("Pick a date first.");
      return;
    }
    const pretty = new Date(bulkDate + "T00:00").toLocaleDateString();
    if (!window.confirm(`Delete EVERY check-in / check-out for ${formatChildName(historyChild)} on ${pretty}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.delete(`/children/${historyChild.id}/attendance-events`, { params: { date: bulkDate } });
      const n = r.data?.deleted ?? 0;
      toast.success(n === 0 ? `No events on ${pretty}.` : `Removed ${n} event${n === 1 ? "" : "s"} on ${pretty}.`);
      setBulkDate("");
      await reloadHistory();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBulkBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [cls, today, tch, imps] = await Promise.all([
        api.get("/classrooms").then((r) => r.data),
        api.get("/attendance/today").then((r) => r.data),
        api.get("/classrooms/teachers").then((r) => r.data).catch(() => ({})),
        api.get("/attendance/imports").then((r) => r.data).catch(() => []),
      ]);
      setClassrooms(cls);
      const m = {};
      for (const e of today) m[e.child_id] = e;
      setTodayMap(m);
      setTeachers(tch || {});
      setImports(imps || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // iter-81 — pull the center's operating_days once so the day toggle default
  // + the "closed day" strike-through pills reflect the center's schedule.
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const r = await api.get("/center/me");
        const op = r.data?.operating_days || "mon-fri";
        if (ignore) return;
        setOperatingDays(op);
        // If the user hasn't manually picked a mode this session, snap the
        // week-mode toggle to the center's actual pattern.
        if (!localStorage.getItem("cc_week_mode")) {
          const modeByOp = { "mon-fri": "5", "sun-sat": "7", "sat-sun": "2" };
          setWeekMode(modeByOp[op] || "5");
        }
      } catch { /* fine — page still works without it */ }
    })();
    const onChange = (e) => {
      const op = e?.detail?.operating_days || "mon-fri";
      setOperatingDays(op);
      const modeByOp = { "mon-fri": "5", "sun-sat": "7", "sat-sun": "2" };
      setWeekMode(modeByOp[op] || "5");
      localStorage.removeItem("cc_week_mode");
    };
    window.addEventListener("cc:operating-days-changed", onChange);
    return () => { ignore = true; window.removeEventListener("cc:operating-days-changed", onChange); };
  }, []);

  const updateTeacher = (room, field, val) => {
    setTeachers((prev) => ({ ...prev, [room]: { ...(prev?.[room] || {}), [field]: val } }));
  };

  const saveTeachers = async () => {
    setSavingTeachers(true);
    try {
      await api.put("/classrooms/teachers", teachers || {});
      toast.success("Teachers saved — DCY 01208 sheets will auto-fill next time.");
      setTeachersOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingTeachers(false);
    }
  };

  const record = async (child_id, kind) => {
    setBusy(child_id);
    try { await api.post("/attendance", { child_id, kind }); toast.success(kind === "check_in" ? "Checked in" : "Checked out"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const downloadWeekly = async (room) => {
    try {
      const params = new URLSearchParams({ week_of: weekOf, days: weekMode });
      if (room && room !== "all") params.append("classroom", room);
      const res = await api.get(`/attendance/weekly/pdf?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setWeekModePersist = (m) => {
    setWeekMode(m);
    try { localStorage.setItem("cc_week_mode", m); } catch (e) { /* sessionStorage may be unavailable */ }
  };

  // ---------------- iter-44 — Excel / CSV import ----------------
  const downloadTemplate = async () => {
    try {
      const res = await api.get("/attendance/template.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "attendance-template.xlsx"; a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadAttendance = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setImportBusy(true); setImportResult(null);
    try {
      const r = await api.post("/attendance/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(r.data);
      toast.success(`Imported ${r.data.inserted} attendance event${r.data.inserted === 1 ? "" : "s"}.`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImportBusy(false);
      if (importFileRef.current) importFileRef.current.value = "";
    }
  };

  const deleteImport = async (batchId, filename) => {
    if (!window.confirm(`Delete the upload "${filename}"? Every attendance event from this batch will be removed.`)) return;
    try {
      const r = await api.delete(`/attendance/imports/${batchId}`);
      toast.success(`Removed ${r.data.events_deleted} event${r.data.events_deleted === 1 ? "" : "s"}.`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // ---------------- iter-44 — edit a single event ----------------
  const openEdit = (childId) => {
    const ev = todayMap[childId];
    if (!ev || ev.kind === "enrolled") {
      toast.error("No event today yet for this child. Use Check in / Check out first.");
      return;
    }
    setEditingChildId(childId);
    setEditKind(ev.kind);
    // Convert ISO → datetime-local format (YYYY-MM-DDTHH:MM)
    try {
      const d = new Date(ev.ts);
      const pad = (n) => String(n).padStart(2, "0");
      setEditTs(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    } catch { setEditTs(""); }
  };

  const saveEdit = async () => {
    const ev = todayMap[editingChildId];
    if (!ev) return;
    try {
      await api.put(`/attendance/events/${ev.id}`, { ts: editTs, kind: editKind });
      toast.success("Attendance updated.");
      setEditingChildId(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteEvent = async () => {
    const ev = todayMap[editingChildId];
    if (!ev) return;
    if (!window.confirm("Delete this attendance event?")) return;
    try {
      await api.delete(`/attendance/events/${ev.id}`);
      toast.success("Attendance event removed.");
      setEditingChildId(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;

  const totalPresent = Object.values(todayMap).filter((e) => e.kind === "check_in").length;
  const totalChildren = classrooms.reduce((s, c) => s + (c.children?.length || 0), 0);

  return (
    <div data-testid="attendance-page" className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Today · {fmtDate(new Date())}</div>
          <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Attendance & classrooms</h1>
          <p className="mt-2 text-stone-500">Currently <strong className="text-emerald-700">{totalPresent}</strong> of {totalChildren} children on-site.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1">Week of  ·  {(() => {
              const sun = new Date(weekOf);
              sun.setDate(sun.getDate() - ((sun.getDay() || 7) % 7));
              const sat = new Date(sun); sat.setDate(sat.getDate() + 6);
              const fmt = (d) => `${d.getMonth()+1}/${d.getDate()}`;
              return `${fmt(sun)}–${fmt(sat)}`;
            })()}</div>
            <div className="inline-flex items-stretch border border-stone-300 rounded-xl bg-white overflow-hidden h-11">
              <button
                data-testid="week-prev-btn"
                type="button"
                onClick={() => {
                  const d = new Date(weekOf);
                  d.setDate(d.getDate() - 7);
                  setWeekOf(mondayOf(d));
                }}
                className="px-3 text-stone-600 hover:bg-stone-50 border-r border-stone-300"
                title="Previous week"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <input
                data-testid="weekly-date"
                type="date"
                value={weekOf}
                onChange={(e) => setWeekOf(mondayOf(e.target.value))}
                className="px-3 text-sm bg-white outline-none"
              />
              <button
                data-testid="week-next-btn"
                type="button"
                onClick={() => {
                  const d = new Date(weekOf);
                  d.setDate(d.getDate() + 7);
                  setWeekOf(mondayOf(d));
                }}
                className="px-3 text-stone-600 hover:bg-stone-50 border-l border-stone-300"
                title="Next week (upcoming)"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                data-testid="week-today-btn"
                type="button"
                onClick={() => setWeekOf(mondayOf(new Date()))}
                className="px-3 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 border-l border-stone-300"
                title="Jump to current week"
              >
                Today
              </button>
            </div>
          </div>
          <div data-testid="week-mode-toggle">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500 mb-1">Days</div>
            <div className="inline-flex border border-stone-300 rounded-xl bg-white overflow-hidden h-11">
              <button
                data-testid="week-mode-5"
                onClick={() => setWeekModePersist("5")}
                className={`px-3 text-sm font-medium transition-colors ${weekMode === "5" ? "bg-emerald-700 text-white" : "text-stone-700 hover:bg-stone-50"}`}
              >
                <CalendarRange className="w-3.5 h-3.5 inline mr-1" /> Mon–Fri
              </button>
              <button
                data-testid="week-mode-7"
                onClick={() => setWeekModePersist("7")}
                className={`px-3 text-sm font-medium transition-colors border-l border-stone-300 ${weekMode === "7" ? "bg-emerald-700 text-white" : "text-stone-700 hover:bg-stone-50"}`}
              >
                Sun–Sat
              </button>
              <button
                data-testid="week-mode-2"
                onClick={() => setWeekModePersist("2")}
                className={`px-3 text-sm font-medium transition-colors border-l border-stone-300 ${weekMode === "2" ? "bg-emerald-700 text-white" : "text-stone-700 hover:bg-stone-50"}`}
                title="Weekend-only care (church nurseries, respite programs)"
              >
                Sat–Sun
              </button>
            </div>
            {/* iter-81 — visual "cross line the rest of the week" indicator */}
            <OperatingDaysStrip weekMode={weekMode} operatingDays={operatingDays} />
          </div>
          <Button data-testid="weekly-pdf-all-btn" onClick={() => downloadWeekly("all")} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
            <FileDown className="w-4 h-4 mr-1" /> DCY 01208 · all classrooms
          </Button>
          <Button
            data-testid="weekly-pdf-next-week-btn"
            variant="outline"
            className="rounded-xl h-11 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            title="Print next week's blank DCY 01208 for filing — one click, all classrooms."
            onClick={() => {
              const d = new Date(weekOf);
              d.setDate(d.getDate() + 7);
              const nextWk = mondayOf(d);
              const params = new URLSearchParams({ week_of: nextWk, days: weekMode });
              params.append("classroom", "all");
              api.get(`/attendance/weekly/pdf?${params.toString()}`, { responseType: "blob" })
                .then((res) => {
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                  window.open(url, "_blank", "noopener,noreferrer");
                  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
                })
                .catch((err) => toast.error(formatApiError(err)));
            }}
          >
            <FileDown className="w-4 h-4 mr-1" /> Print next week's blank
          </Button>
          <Button
            data-testid="weekly-email-archive-btn"
            variant="outline"
            className="rounded-xl h-11 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            title="Email the selected week's DCY 01208 archive (every classroom, multi-page) to the center owner. The Friday scheduler also fires automatically each week."
            onClick={async () => {
              try {
                const r = await api.post("/attendance/weekly/email-archive", null, { params: { week_of: weekOf } });
                if (r?.data?.sent) toast.success(`Emailed the attendance archive for ${weekOf} to the owner.`);
                else toast.warning(`Couldn't send — ${r?.data?.reason || "unknown error"}.`);
              } catch (err) { toast.error(formatApiError(err)); }
            }}
          >
            <Mail className="w-4 h-4 mr-1" /> Email selected week
          </Button>
          <Button data-testid="attendance-import-btn" variant="outline" onClick={() => { setImportResult(null); setImportDialogOpen(true); }} className="rounded-xl h-11 border-emerald-300 text-emerald-800 hover:bg-emerald-50">
            <Upload className="w-4 h-4 mr-1" /> Import Excel
          </Button>
          <Button data-testid="assign-teachers-btn" variant="outline" onClick={() => setTeachersOpen((v) => !v)} className="rounded-xl h-11">
            <UserCog className="w-4 h-4 mr-1" /> {teachersOpen ? "Close" : "Assign teachers"}
          </Button>
        </div>
      </header>

      {teachersOpen && (
        <div data-testid="teacher-assign-panel" className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">Classroom teachers</div>
              <p className="text-xs text-stone-500 mt-1">Lead + Assistant names auto-fill the "Teacher(s)" field on every DCY 01208 weekly sheet.</p>
            </div>
            <Button data-testid="save-teachers-btn" onClick={saveTeachers} disabled={savingTeachers} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
              {savingTeachers ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
            </Button>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
            {FIXED_CLASSROOMS.map((room) => {
              const slug = room.toLowerCase().replace(/\s+/g, "-");
              const entry = teachers?.[room] || {};
              return (
                <div key={room} className="rounded-xl bg-white border border-stone-200 p-3 space-y-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-stone-700">{room}</div>
                  <Input
                    data-testid={`teacher-lead-${slug}`}
                    placeholder="Lead teacher"
                    value={entry.lead || ""}
                    onChange={(e) => updateTeacher(room, "lead", e.target.value)}
                    className="h-9 text-sm"
                  />
                  <Input
                    data-testid={`teacher-assistant-${slug}`}
                    placeholder="Assistant teacher"
                    value={entry.assistant || ""}
                    onChange={(e) => updateTeacher(room, "assistant", e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {imports.length > 0 && (
        <div data-testid="attendance-imports-panel" className="rounded-2xl border border-stone-200 bg-white">
          <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <FileSpreadsheet className="w-4 h-4 text-emerald-700 shrink-0" />
              <div className="min-w-0">
                <div className="font-display text-base text-stone-900">Uploaded attendance files</div>
                <div className="text-xs text-stone-500">{imports.length} batch{imports.length === 1 ? "" : "es"} on file. Deleting a batch removes every event it inserted.</div>
              </div>
            </div>
          </div>
          <ul className="divide-y divide-stone-100">
            {imports.map((b) => {
              const dist = b.classroom_distribution || {};
              const distSummary = Object.entries(dist).map(([k, v]) => `${v} ${k}`).join(" · ") || "—";
              return (
                <li key={b.id} data-testid={`attendance-import-row-${b.id}`} className="px-5 py-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-stone-900 truncate">{b.filename}</div>
                    <div className="text-xs text-stone-500">
                      Uploaded {fmtDate(b.uploaded_at)} by {b.uploaded_by} · {b.events_inserted} event{b.events_inserted === 1 ? "" : "s"} · {distSummary}
                      {b.skipped_count ? ` · ${b.skipped_count} skipped` : ""}
                    </div>
                  </div>
                  <Button
                    data-testid={`attendance-import-delete-${b.id}`}
                    variant="outline" size="sm"
                    onClick={() => deleteImport(b.id, b.filename)}
                    className="rounded-xl border-red-300 text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete batch
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <Tabs value={activeRoom} onValueChange={setActiveRoom}>
        {/* iter-81d — "CLOSED today" banner: fires when today is either a
            non-operating day of the week OR an observed holiday. Sits above
            the classroom tabs so staff instantly see no check-ins should
            happen today. */}
        {(() => {
          const todayIdx = new Date().getDay(); // Sun=0..Sat=6
          const open = new Set(OP_DAYS_BY_CENTER[operatingDays] || [1,2,3,4,5]);
          const isNonOpDay = !open.has(todayIdx);
          const isHoliday = !!holidayToday;
          if (!isNonOpDay && !isHoliday) return null;
          const dayLabel = operatingDays === "mon-fri" ? "Mon–Fri"
                      : operatingDays === "sat-sun" ? "Sat–Sun"
                      : "Sun–Sat";
          return (
            <div
              data-testid="attendance-closed-today-banner"
              data-holiday={holidayToday || ""}
              className="mb-4 rounded-2xl border-2 border-dashed border-red-300 bg-red-50/70 px-5 py-4 flex flex-wrap items-center gap-3 animate-pulse"
              role="status"
            >
              <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-red-600 text-white font-black text-lg">✕</span>
              <div className="min-w-0">
                <div className="font-display text-lg font-semibold text-red-800">
                  {isHoliday ? `Center is CLOSED today — ${holidayToday}` : "Center is CLOSED today"}
                </div>
                <div className="text-xs text-red-700/90">
                  {isHoliday
                    ? <>Observed holiday. No check-ins should happen today. Weekly DCY 01208 marks this day as HOLIDAY — {holidayToday}.</>
                    : <>Operating days: <strong>{dayLabel}</strong>. No check-ins should happen today. Weekly DCY 01208 renders this day as CLOSED.</>}
                </div>
              </div>
            </div>
          );
        })()}
        <TabsList className="bg-stone-100 p-1 rounded-xl h-auto flex-wrap gap-1">
          {FIXED_CLASSROOMS.map((room) => {
            const cls = classrooms.find((c) => c.name === room);
            const count = cls?.children?.length || 0;
            return (
              <TabsTrigger key={room} value={room} data-testid={`classroom-tab-${room.toLowerCase().replace(/\s+/g, "-")}`}
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 font-medium">
                {room} <span className="ml-2 text-[10px] font-bold text-stone-500">({count})</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {FIXED_CLASSROOMS.map((room) => {
          const cls = classrooms.find((c) => c.name === room);
          // Belt-and-suspenders alphabetical sort by Last → First (iter-47).
          const kids = [...(cls?.children || [])].sort((a, b) => {
            const al = (a.last_name || "").toLowerCase();
            const bl = (b.last_name || "").toLowerCase();
            if (al !== bl) return al < bl ? -1 : 1;
            const af = (a.first_name || "").toLowerCase();
            const bf = (b.first_name || "").toLowerCase();
            return af < bf ? -1 : af > bf ? 1 : 0;
          });
          const presentInRoom = kids.filter((c) => todayMap[c.id]?.kind === "check_in").length;
          return (
            <TabsContent key={room} value={room} className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-stone-700">
                  <Users className="w-4 h-4 text-stone-500" />
                  <span><strong className="text-emerald-700">{presentInRoom}</strong> of {kids.length} present in {room}</span>
                </div>
                <Button data-testid={`weekly-pdf-${room.toLowerCase().replace(/\s+/g, "-")}-btn`} variant="outline" size="sm" onClick={() => downloadWeekly(room)} className="rounded-xl">
                  <Calendar className="w-3.5 h-3.5 mr-1" /> Weekly DCY 01208 · {room}
                </Button>
              </div>

              <div className="rounded-2xl border border-stone-200 bg-white">
                {kids.length === 0 ? (
                  <div className="py-14 text-center text-sm text-stone-500">No children enrolled in {room} yet.</div>
                ) : (
                  <div className="divide-y divide-stone-100">
                    {kids.map((c) => {
                      const last = todayMap[c.id];
                      const isIn = last?.kind === "check_in";
                      return (
                        <div key={c.id} data-testid={`attendance-row-${c.id}`} className="px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="font-medium text-stone-900">{formatChildName(c)}</div>
                            <div className="text-xs text-stone-500">{AGE_LABEL[c.age_group] || c.age_group}{last && last.kind !== "enrolled" ? ` · last ${last.kind === "check_in" ? "checked in" : "checked out"} ${new Date(last.ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })}` : ""}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button data-testid={`attendance-in-${c.id}`} disabled={busy === c.id || isIn} onClick={() => record(c.id, "check_in")} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl">
                              <LogIn className="w-4 h-4 mr-1" /> Check in
                            </Button>
                            <Button data-testid={`attendance-out-${c.id}`} disabled={busy === c.id || !isIn} onClick={() => record(c.id, "check_out")} variant="outline" className="rounded-xl">
                              <LogOut className="w-4 h-4 mr-1" /> Check out
                            </Button>
                            {last && last.kind !== "enrolled" && (
                              <Popover open={editingChildId === c.id} onOpenChange={(o) => { if (!o) setEditingChildId(null); else openEdit(c.id); }}>
                                <PopoverTrigger asChild>
                                  <Button data-testid={`attendance-edit-${c.id}`} variant="ghost" size="icon" className="rounded-xl text-stone-500 hover:text-emerald-800" title="Edit latest event">
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-80">
                                  <div className="space-y-3">
                                    <div className="text-xs uppercase tracking-widest font-semibold text-stone-500">Edit attendance</div>
                                    <div>
                                      <Label className="text-xs">Time</Label>
                                      <Input
                                        data-testid={`edit-ts-input-${c.id}`}
                                        type="datetime-local"
                                        value={editTs}
                                        onChange={(e) => setEditTs(e.target.value)}
                                        className="mt-1 h-10"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Kind</Label>
                                      <select
                                        data-testid={`edit-kind-select-${c.id}`}
                                        value={editKind}
                                        onChange={(e) => setEditKind(e.target.value)}
                                        className="mt-1 w-full border border-stone-300 rounded-md h-10 px-2 text-sm"
                                      >
                                        <option value="check_in">Check in</option>
                                        <option value="check_out">Check out</option>
                                      </select>
                                    </div>
                                    <div className="flex items-center justify-between gap-2 pt-1">
                                      <Button data-testid={`edit-delete-btn-${c.id}`} variant="ghost" size="sm" onClick={deleteEvent} className="text-red-600 hover:bg-red-50">
                                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                      </Button>
                                      <Button data-testid={`edit-save-btn-${c.id}`} size="sm" onClick={saveEdit} className="rounded-xl bg-emerald-700 hover:bg-emerald-800">
                                        Save
                                      </Button>
                                    </div>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                            <Button
                              data-testid={`attendance-history-${c.id}`}
                              variant="ghost"
                              size="icon"
                              className="rounded-xl text-stone-500 hover:text-emerald-800"
                              title="View all attendance times"
                              onClick={() => openHistory(c)}
                            >
                              <History className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* iter-44 — Excel/CSV import dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import attendance from Excel</DialogTitle>
            <DialogDescription>
              Upload a .xlsx or .csv with columns <b>Name · Date · Time In · Time Out</b>. Each row is matched to an enrolled child by name and routed to their classroom automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button data-testid="attendance-template-btn" variant="outline" size="sm" onClick={downloadTemplate} className="rounded-xl">
              <FileDown className="w-3.5 h-3.5 mr-1" /> Download blank template (every enrolled child pre-listed)
            </Button>
            <div className="rounded-xl border border-dashed border-stone-300 p-6 text-center">
              <input
                ref={importFileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                data-testid="attendance-import-file-input"
                onChange={(e) => uploadAttendance(e.target.files?.[0])}
              />
              <FileSpreadsheet className="w-8 h-8 mx-auto text-stone-400" />
              <p className="mt-2 text-sm text-stone-700">Drop your file or pick from disk.</p>
              <Button
                data-testid="attendance-import-pick-btn"
                disabled={importBusy}
                onClick={() => importFileRef.current?.click()}
                className="mt-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl"
              >
                {importBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                Choose file
              </Button>
            </div>
            {importResult && (
              <div data-testid="attendance-import-result" className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 space-y-1">
                <div className="font-semibold">Imported {importResult.inserted} event{importResult.inserted === 1 ? "" : "s"}.</div>
                {Object.keys(importResult.classroom_distribution || {}).length > 0 && (
                  <div className="text-xs">
                    Classrooms: {Object.entries(importResult.classroom_distribution).map(([k, v]) => `${v} ${k}`).join(" · ")}
                  </div>
                )}
                {importResult.skipped > 0 && (
                  <div className="text-xs text-amber-800">
                    {importResult.skipped} row{importResult.skipped === 1 ? " was" : "s were"} skipped (likely names not matching enrolled children).
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImportDialogOpen(false)} className="rounded-xl">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All attendance times for one child — edit/delete any historical event */}
      <Dialog open={!!historyChild} onOpenChange={(o) => { if (!o) { setHistoryChild(null); setHistoryEvents([]); setHistoryRowDraft({}); setBulkDate(""); } }}>
        <DialogContent data-testid="attendance-history-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All attendance times{historyChild ? ` · ${formatChildName(historyChild)}` : ""}</DialogTitle>
            <DialogDescription>
              Every check-in and check-out for this child. Edit a row's time / kind, or delete one entirely — changes are saved immediately.
            </DialogDescription>
          </DialogHeader>

          {/* Bulk delete a whole day — useful for holidays / botched imports */}
          {historyChild && (
            <div data-testid="attendance-history-bulk" className="rounded-xl border border-red-200 bg-red-50/40 p-3 flex flex-wrap items-end gap-2 mb-2">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-[11px] uppercase tracking-widest text-red-700 font-semibold">Delete an entire day</Label>
                <Input
                  data-testid="attendance-history-bulk-date"
                  type="date"
                  value={bulkDate}
                  onChange={(e) => setBulkDate(e.target.value)}
                  className="mt-1 h-9 max-w-[200px]"
                />
              </div>
              <Button
                data-testid="attendance-history-bulk-btn"
                disabled={!bulkDate || bulkBusy}
                onClick={bulkDeleteDay}
                className="bg-red-700 hover:bg-red-800 text-white rounded-lg h-9 text-xs"
              >
                {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                Delete every event on this date
              </Button>
            </div>
          )}
          {historyLoading ? (
            <div className="py-8 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
          ) : historyEvents.length === 0 ? (
            <div className="py-8 text-center text-sm text-stone-500 italic">No attendance times recorded yet for this child.</div>
          ) : (
            <div className="border border-stone-200 rounded-xl overflow-hidden">
              <table data-testid="attendance-history-table" className="w-full text-sm">
                <thead className="bg-stone-50 text-[11px] uppercase tracking-widest text-stone-500">
                  <tr>
                    <th className="text-left px-3 py-2">Date / Time</th>
                    <th className="text-left px-3 py-2">Kind</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {historyEvents.map((ev) => {
                    const draft = historyRowDraft[ev.id];
                    const editing = !!draft;
                    return (
                      <tr key={ev.id} data-testid={`attendance-history-row-${ev.id}`} className="border-t border-stone-100">
                        <td className="px-3 py-2">
                          {editing ? (
                            <Input
                              data-testid={`attendance-history-ts-${ev.id}`}
                              type="datetime-local"
                              value={draft.ts}
                              onChange={(e) => setHistoryRowDraft((d) => ({ ...d, [ev.id]: { ...draft, ts: e.target.value } }))}
                              className="h-9 max-w-[230px]"
                            />
                          ) : (
                            <div className="text-stone-800">{new Date(ev.ts).toLocaleString()}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {editing ? (
                            <select
                              data-testid={`attendance-history-kind-${ev.id}`}
                              value={draft.kind}
                              onChange={(e) => setHistoryRowDraft((d) => ({ ...d, [ev.id]: { ...draft, kind: e.target.value } }))}
                              className="border border-stone-300 rounded-md h-9 px-2 text-sm"
                            >
                              <option value="check_in">Check in</option>
                              <option value="check_out">Check out</option>
                            </select>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider ${ev.kind === "check_in" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>
                              {ev.kind === "check_in" ? <LogIn className="w-3 h-3" /> : <LogOut className="w-3 h-3" />}
                              {ev.kind === "check_in" ? "Check in" : "Check out"}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {editing ? (
                            <>
                              <Button
                                data-testid={`attendance-history-save-${ev.id}`}
                                size="sm"
                                disabled={historySavingId === ev.id}
                                onClick={() => saveHistoryRow(ev)}
                                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg h-7 text-xs mr-1"
                              >
                                {historySavingId === ev.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
                                Save
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setHistoryRowDraft((d) => { const c = { ...d }; delete c[ev.id]; return c; })}
                                className="rounded-lg h-7 text-xs text-stone-600"
                              >Cancel</Button>
                            </>
                          ) : (
                            <>
                              <Button
                                data-testid={`attendance-history-edit-${ev.id}`}
                                variant="outline"
                                size="sm"
                                onClick={() => editHistoryRow(ev)}
                                className="rounded-lg h-7 text-xs mr-1 border-stone-300"
                              ><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
                              <Button
                                data-testid={`attendance-history-delete-${ev.id}`}
                                variant="outline"
                                size="sm"
                                disabled={historySavingId === ev.id}
                                onClick={() => deleteHistoryRow(ev)}
                                className="rounded-lg h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                              ><Trash2 className="w-3 h-3" /></Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHistoryChild(null)} className="rounded-xl">
              <X className="w-4 h-4 mr-1" /> Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
